import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotifications,
  markNotificationsRead,
  markAllNotificationsRead,
  deleteNotification,
  clearAllNotifications,
} from "../../../api/system/notifications";
import dayjs from "../../../utils/dayjs";
import Pagination from "../../../components/common/Pagination";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { socket } from "../../../utils/socket";
import { resolveNotificationTarget } from "../../../utils/notificationNavigation";
import {
  Bell,
  CheckCheck,
  Trash2,
  X,
  Inbox,
} from "lucide-react";

function NotificationModal({ item, onClose, onRead }) {
  useEffect(() => {
    if (!item.is_read) onRead(item.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-[220] flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative flex max-h-[85dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:rounded-[1.75rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Chi tiết thông báo
            </p>
            <h3 className="mt-1 text-base font-black leading-snug text-gray-900 sm:text-lg">
              {item.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            aria-label="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {item.message ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
              {item.message}
            </p>
          ) : (
            <p className="text-sm italic text-gray-400">Không có nội dung</p>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50/80 px-5 py-3 text-[11px] text-gray-500">
          <div>
            Gửi lúc: {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
          </div>
          {item.sender_name ? (
            <div className="mt-0.5">Từ: {item.sender_name}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [activeItem, setActiveItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotifications({
        page,
        limit: pageSize,
        status,
      });
      setItems(res.items || []);
      setTotal(Number(res.total) || 0);
    } catch (err) {
      console.error(err);
      showErrorToast(err?.message || "Không thể tải thông báo");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handleRealtime = () => load();
    socket.on("notification:new", handleRealtime);
    return () => socket.off("notification:new", handleRealtime);
  }, [load]);

  const markAllRead = async () => {
    const result = await showConfirmDialog({
      title: "Đánh dấu tất cả đã đọc?",
      text: "Bạn có chắc muốn đánh dấu tất cả thông báo là đã đọc?",
      confirmText: "Đánh dấu",
      cancelText: "Hủy",
    });
    if (!result.isConfirmed) return;

    try {
      await markAllNotificationsRead();
      window.dispatchEvent(new Event("refresh-notification-count"));
      await load();
      showSuccessToast("Đã đánh dấu tất cả thông báo là đã đọc!");
    } catch {
      showErrorToast("Không thể cập nhật trạng thái thông báo");
    }
  };

  const removeOne = async (id) => {
    try {
      await deleteNotification(id);
      window.dispatchEvent(new Event("refresh-notification-count"));
      await load();
    } catch {
      showErrorToast("Không thể xóa thông báo");
    }
  };

  const removeAll = async () => {
    const result = await confirmDelete({
      type: DELETE_ENTITY.notification,
      name: "tất cả thông báo",
      extraLines:
        total > 0
          ? [`Sẽ xóa toàn bộ ${total} thông báo trong hộp thư của bạn.`]
          : [],
      confirmText: "Xóa hết",
    });
    if (!result.isConfirmed) return;

    try {
      await clearAllNotifications();
      window.dispatchEvent(new Event("refresh-notification-count"));
      setPage(1);
      await load();
      showSuccessToast("Đã xóa toàn bộ thông báo!");
    } catch {
      showErrorToast("Không thể xóa thông báo");
    }
  };

  const openNotificationTarget = async (item) => {
    if (!item) return;

    try {
      if (!item.is_read) {
        await markNotificationsRead([item.id]);
        window.dispatchEvent(new Event("refresh-notification-count"));
      }
    } catch (err) {
      console.error("Không thể đánh dấu đã đọc trước khi điều hướng:", err);
    }

    const target = resolveNotificationTarget(item);
    if (!target?.path || !target?.referenceId) {
      setActiveItem(item);
      await load();
      return;
    }

    await load();
    navigate(target.path, {
      state: {
        notificationTarget: {
          moduleCode: target.moduleCode,
          referenceId: target.referenceId,
          targetType: target.targetType,
          notificationId: item.id,
        },
      },
    });
  };

  return (
    <div className="mx-auto min-h-full w-full max-w-5xl px-4 pb-28 pt-4 sm:px-6 sm:pb-8 sm:pt-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-700">
                <Bell size={18} />
              </span>
              <h1 className="text-xl font-black tracking-tight text-gray-900 sm:text-2xl">
                Thông báo
              </h1>
            </div>
            <p className="pl-11 text-[12px] text-gray-500 sm:pl-0 sm:text-sm">
              {total > 0
                ? `${total} thông báo trong hộp thư`
                : "Hộp thư hệ thống của bạn"}
            </p>
          </div>
        </div>

        {/* Filters + actions */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            {[
              { value: "all", label: "Tất cả" },
              { value: "unread", label: "Chưa đọc" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setStatus(opt.value);
                  setPage(1);
                }}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide transition sm:flex-none sm:px-4 ${
                  status === opt.value
                    ? "bg-red-700 text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-900 px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-sm active:scale-[0.98]"
            >
              <CheckCheck size={15} />
              Đọc hết
            </button>
            <button
              type="button"
              onClick={removeAll}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-sm active:scale-[0.98]"
            >
              <Trash2 size={15} />
              Xóa hết
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {loading ? (
          <div className="space-y-0 divide-y divide-gray-50">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse px-4 py-4 sm:px-6">
                <div className="mb-2 h-4 w-2/3 rounded bg-gray-100" />
                <div className="h-3 w-full rounded bg-gray-50" />
                <div className="mt-2 h-3 w-1/3 rounded bg-gray-50" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <Inbox className="mx-auto mb-3 text-gray-300" size={28} />
            <p className="text-sm font-medium text-gray-400">
              {status === "unread"
                ? "Không còn thông báo chưa đọc"
                : "Chưa có thông báo"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {items.map((n) => (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                onClick={() => openNotificationTarget(n)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openNotificationTarget(n);
                  }
                }}
                className={`cursor-pointer px-4 py-3.5 transition active:bg-gray-50 sm:px-6 sm:py-4 ${
                  !n.is_read ? "bg-red-50/70" : "bg-white hover:bg-gray-50/80"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      !n.is_read ? "bg-red-600" : "bg-transparent"
                    }`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3
                        className={`text-[15px] leading-snug sm:text-base ${
                          !n.is_read
                            ? "font-bold text-gray-900"
                            : "font-semibold text-gray-600"
                        }`}
                      >
                        {n.title}
                      </h3>
                      <span className="shrink-0 text-[11px] font-medium text-gray-400">
                        {dayjs(n.created_at).fromNow()}
                      </span>
                    </div>

                    {!n.is_read ? (
                      <span className="mt-1.5 inline-flex rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-white">
                        Mới
                      </span>
                    ) : null}

                    {n.message ? (
                      <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-gray-600 sm:text-sm">
                        {n.message}
                      </p>
                    ) : null}

                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                      {n.sender_name ? (
                        <span className="italic">Từ: {n.sender_name}</span>
                      ) : null}
                      {n.module_code ? (
                        <span className="rounded-md bg-gray-100 px-2 py-0.5 font-medium text-gray-500">
                          {n.module_code}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <button
                    type="button"
                    title="Xóa"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeOne(n.id);
                    }}
                    className="mt-0.5 shrink-0 rounded-xl border border-transparent p-2 text-gray-300 transition hover:border-red-100 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {total > 0 ? (
        <div className="mt-4">
          <Pagination
            totalRecords={total}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      ) : null}

      {activeItem ? (
        <NotificationModal
          item={activeItem}
          onClose={() => setActiveItem(null)}
          onRead={async (id) => {
            await markNotificationsRead([id]);
            window.dispatchEvent(new Event("refresh-notification-count"));
            await load();
          }}
        />
      ) : null}
    </div>
  );
}
